/**
 * User Management Service
 *
 * Handles API calls for organization user management (manager/admin operations).
 */

const API_URL = process.env.REACT_APP_API_URL || '';

/**
 * Get current user info from Azure SWA authentication or demo session
 */
const getCurrentUser = async () => {
  // Check for demo session first
  const demoToken = sessionStorage.getItem('demoToken');
  if (demoToken) {
    const demoSession = sessionStorage.getItem('demoSession');
    if (demoSession) {
      try {
        const session = JSON.parse(demoSession);
        return {
          id: session.user.user_id,
          email: session.user.email,
          name: session.user.name,
          provider: 'demo',
          roles: [],
          isDemoMode: true
        };
      } catch (e) {
        console.error('Error parsing demo session:', e);
      }
    }
  }

  // OAuth authentication
  try {
    const response = await fetch('/.auth/me');
    if (response.ok) {
      const authData = await response.json();
      const userInfo = authData.clientPrincipal;
      if (userInfo) {
        return {
          id: userInfo.userId,
          email: userInfo.userDetails,
          name: userInfo.userClaims?.find(claim => claim.typ === 'name')?.val || userInfo.userDetails,
          provider: userInfo.identityProvider,
          roles: userInfo.userRoles,
          isDemoMode: false
        };
      }
    }
  } catch (error) {
    console.error('Error fetching user info:', error);
  }
  return null;
};

/**
 * List all users in an organization
 * @param {string} orgId - Organization ID
 * @returns {Promise<Array>} List of users in the organization
 */
export const listOrganizationUsers = async (orgId) => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    // Add demo token or OAuth email
    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organizations/${orgId}/users`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to list users`);
    }

    const data = await response.json();
    // Backend returns array directly, not wrapped in {"users": [...]}
    return Array.isArray(data) ? data : [];
  } catch (error) {
    console.error('Error listing organization users:', error);
    throw error;
  }
};

/**
 * Promote a user to manager role
 * @param {string} orgId - Organization ID
 * @param {string} userId - User ID to promote
 * @returns {Promise<Object>} Updated user object
 */
export const promoteUserToManager = async (orgId, userId) => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organizations/${orgId}/users/${userId}/promote`, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to promote user`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error promoting user to manager:', error);
    throw error;
  }
};

/**
 * Demote a manager to regular user role
 * @param {string} orgId - Organization ID
 * @param {string} userId - User ID to demote
 * @returns {Promise<Object>} Updated user object
 */
export const demoteManagerToUser = async (orgId, userId) => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organizations/${orgId}/users/${userId}/demote`, {
      method: 'POST',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to demote user`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error demoting manager to user:', error);
    throw error;
  }
};

/**
 * Delete a user and all their data permanently
 * WARNING: This action is irreversible and deletes all user data including sessions and interviews
 * @param {string} orgId - Organization ID
 * @param {string} userId - User ID to delete
 * @returns {Promise<Object>} Deletion confirmation
 */
export const deleteUser = async (orgId, userId) => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organizations/${orgId}/users/${userId}`, {
      method: 'DELETE',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to delete user`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
};

/**
 * Get user's invite link (generates token on-demand if missing)
 * @param {string} orgId - Organization ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Invite link data (token, relative_url, invite_count, created_at)
 */
export const getUserInviteLink = async (orgId, userId) => {
  try {
    const user = await getCurrentUser();
    if (!user || !user.email) {
      throw new Error('User not authenticated');
    }

    const headers = {
      'Content-Type': 'application/json'
    };

    if (user.isDemoMode) {
      const demoToken = sessionStorage.getItem('demoToken');
      if (demoToken) {
        headers['Authorization'] = `Bearer ${demoToken}`;
      }
    } else {
      headers['X-User-Email'] = user.email;
    }

    const response = await fetch(`${API_URL}/api/organizations/${orgId}/users/${userId}/invite-link`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP ${response.status}: Failed to get invite link`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error getting invite link:', error);
    throw error;
  }
};
